import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/store';
import { useAuthStore } from '@/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Eye, EyeOff, Loader2, ChevronRight, Check, Mail, Lock, User, Shield, ArrowLeft, AlertCircle, Sparkles } from 'lucide-react';

const STEPS = [
  { id: 'username', label: 'Escolha seu usuario', icon: User, placeholder: 'Digite seu usuario', type: 'text' },
  { id: 'password', label: 'Crie uma senha segura', icon: Lock, placeholder: 'Minimo 4 caracteres', type: 'password' },
  { id: 'confirmPassword', label: 'Confirme sua senha', icon: Shield, placeholder: 'Repita sua senha', type: 'password' },
  { id: 'email', label: 'Seu melhor email', icon: Mail, placeholder: 'email@exemplo.com', type: 'email' },
];

function GlitchText({ text, className }) {
  return (
    <span className={`relative inline-block ${className}`}>
      <span className="relative z-10">{text}</span>
      <span className="absolute top-0 left-0.5 text-cyan-500/30 z-0 animate-glitch-1" aria-hidden="true">{text}</span>
      <span className="absolute top-0 -left-0.5 text-primary/30 z-0 animate-glitch-2" aria-hidden="true">{text}</span>
    </span>
  );
}

function ProgressDots({ current, total }) {
  return (
    <div className="flex items-center gap-2 justify-center">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className="relative">
          <div className={`h-2 rounded-full transition-all duration-500 ease-out ${
            i < current ? 'w-8 bg-primary' : i === current ? 'w-8 bg-primary animate-pulse' : 'w-2 bg-white/10'
          }`} />
          {i === current && <div className="absolute inset-0 h-2 w-8 rounded-full bg-primary/40 blur-sm animate-pulse" />}
        </div>
      ))}
    </div>
  );
}

function TypewriterText({ text, onComplete, speed = 30 }) {
  const [displayed, setDisplayed] = useState('');
  const idx = useRef(0);
  useEffect(() => {
    setDisplayed('');
    idx.current = 0;
    const timer = setInterval(() => {
      if (idx.current < text.length) {
        setDisplayed(text.slice(0, idx.current + 1));
        idx.current++;
      } else {
        clearInterval(timer);
        onComplete?.();
      }
    }, speed);
    return () => clearInterval(timer);
  }, [text]);
  return <>{displayed}<span className="animate-blink">|</span></>;
}

function ParticleBg() {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {Array.from({ length: 30 }).map((_, i) => (
        <div key={i} className="absolute rounded-full bg-primary/20" style={{
          width: `${Math.random() * 4 + 1}px`, height: `${Math.random() * 4 + 1}px`,
          left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
          animation: `float-particle ${Math.random() * 10 + 8}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 5}s`
        }} />
      ))}
      <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-[120px]" />
    </div>
  );
}

export default function RegisterPage() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0-3: questions, 4: review, 5: email-confirm, 6: code, 7: first-login
  const [values, setValues] = useState({ username: '', password: '', confirmPassword: '', email: '' });
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [showPwModal, setShowPwModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [linkInfo, setLinkInfo] = useState(null);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [animDir, setAnimDir] = useState('next');
  const [inputReady, setInputReady] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { validateLink(); }, [inviteCode]);

  const validateLink = async () => {
    try {
      const r = await api.get(`/invite-links/validate/${inviteCode}`);
      setLinkInfo(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Link invalido ou expirado');
    } finally { setValidating(false); }
  };

  useEffect(() => {
    if (step < 4) {
      setInputReady(false);
      const t = setTimeout(() => { setInputReady(true); inputRef.current?.focus(); }, 400);
      return () => clearTimeout(t);
    }
  }, [step]);

  const canProceed = () => {
    const v = values[STEPS[step]?.id];
    if (step === 0) return v.trim().length >= 2;
    if (step === 1) return v.length >= 4;
    if (step === 2) return v === values.password && v.length >= 4;
    if (step === 3) return v.includes('@') && v.includes('.');
    return true;
  };

  const handleNext = () => {
    if (step < 3) {
      if (!canProceed()) {
        if (step === 0) toast.error('Usuario deve ter pelo menos 2 caracteres');
        if (step === 1) toast.error('Senha deve ter pelo menos 4 caracteres');
        if (step === 2) toast.error('As senhas nao coincidem');
        if (step === 3) toast.error('Email invalido');
        return;
      }
      setAnimDir('next');
      setStep(step + 1);
    } else if (step === 3) {
      if (!canProceed()) { toast.error('Email invalido'); return; }
      setAnimDir('next');
      setStep(4); // review
    }
  };

  const handleBack = () => {
    if (step > 0 && step <= 4) { setAnimDir('prev'); setStep(step - 1); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleNext(); };

  const handleConfirmEmail = () => { setStep(5); }; // email confirm dialog
  const handleProceedRegister = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/auth/register/${inviteCode}`, {
        username: values.username.trim(),
        email: values.email.trim().toLowerCase(),
        password: values.password
      });
      if (r.data.requires_verification) {
        setUserId(r.data.user_id);
        setStep(6); // code step
      } else if (r.data.token) {
        localStorage.setItem('nexus-token', r.data.token);
        setStep(7); // first login
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar conta');
      setStep(4); // back to review
    } finally { setLoading(false); }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) { toast.error('Digite o codigo de 6 digitos'); return; }
    setVerifying(true);
    try {
      const r = await api.post('/auth/verify-registration', { user_id: userId, code: code.trim() });
      if (r.data.token) {
        localStorage.setItem('nexus-token', r.data.token);
        setStep(7); // first login modal
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Codigo invalido'); }
    finally { setVerifying(false); }
  };

  const handleResend = async () => {
    setResending(true);
    try { await api.post('/auth/resend-verification', { user_id: userId }); toast.success('Codigo reenviado!'); }
    catch { toast.error('Erro ao reenviar'); }
    finally { setResending(false); }
  };

  const handleFinish = () => {
    window.location.href = '/dashboard';
  };

  const trialLabel = linkInfo?.test_hours >= 24
    ? `${Math.floor(linkInfo.test_hours / 24)} dia${Math.floor(linkInfo.test_hours / 24) > 1 ? 's' : ''} de teste gratis`
    : `${linkInfo?.test_hours || 24} horas de teste gratis`;

  // --- LOADING ---
  if (validating) return (
    <div className="min-h-screen bg-[#050a15] flex items-center justify-center">
      <ParticleBg />
      <div className="relative z-10 flex flex-col items-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
        <p className="text-white/40 font-mono text-sm tracking-wider">Validando convite...</p>
      </div>
    </div>
  );

  // --- ERROR ---
  if (error) return (
    <div className="min-h-screen bg-[#050a15] flex items-center justify-center p-4">
      <ParticleBg />
      <div className="relative z-10 text-center max-w-sm">
        <div className="w-20 h-20 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="w-10 h-10 text-red-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">Link Invalido</h2>
        <p className="text-white/50 mb-8">{error}</p>
        <Button onClick={() => navigate('/login')} variant="outline" className="rounded-xl h-12 px-8 border-white/10 hover:border-primary/30">
          Voltar para Login
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050a15] flex items-center justify-center p-4 relative overflow-hidden">
      <Toaster position="top-center" richColors />
      <ParticleBg />

      <style>{`
        @keyframes float-particle { 0%,100%{transform:translateY(0) translateX(0)} 25%{transform:translateY(-20px) translateX(10px)} 50%{transform:translateY(-10px) translateX(-10px)} 75%{transform:translateY(-30px) translateX(5px)} }
        @keyframes glitch-1 { 0%,100%{clip-path:inset(0 0 0 0)} 20%{clip-path:inset(20% 0 60% 0);transform:translate(2px,-2px)} 40%{clip-path:inset(50% 0 20% 0);transform:translate(-2px,2px)} }
        @keyframes glitch-2 { 0%,100%{clip-path:inset(0 0 0 0)} 30%{clip-path:inset(30% 0 40% 0);transform:translate(-2px,1px)} 60%{clip-path:inset(60% 0 10% 0);transform:translate(2px,-1px)} }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes slideInRight { from{opacity:0;transform:translateX(60px)} to{opacity:1;transform:translateX(0)} }
        @keyframes slideInLeft { from{opacity:0;transform:translateX(-60px)} to{opacity:1;transform:translateX(0)} }
        @keyframes slideOutLeft { from{opacity:1;transform:translateX(0)} to{opacity:0;transform:translateX(-60px)} }
        @keyframes scaleIn { from{opacity:0;transform:scale(0.9)} to{opacity:1;transform:scale(1)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes successPulse { 0%{box-shadow:0 0 0 0 rgba(16,185,129,0.4)} 70%{box-shadow:0 0 0 20px rgba(16,185,129,0)} 100%{box-shadow:0 0 0 0 rgba(16,185,129,0)} }
        .animate-blink { animation: blink 0.8s step-end infinite; }
        .animate-glitch-1 { animation: glitch-1 3s ease-in-out infinite; }
        .animate-glitch-2 { animation: glitch-2 3s ease-in-out infinite reverse; }
        .anim-slide-next { animation: slideInRight 0.4s cubic-bezier(.16,1,.3,1) forwards; }
        .anim-slide-prev { animation: slideInLeft 0.4s cubic-bezier(.16,1,.3,1) forwards; }
        .anim-scale-in { animation: scaleIn 0.5s cubic-bezier(.16,1,.3,1) forwards; }
        .anim-fade-up { animation: fadeUp 0.5s cubic-bezier(.16,1,.3,1) forwards; }
        .anim-success-pulse { animation: successPulse 1.5s infinite; }
        .code-input:focus { box-shadow: 0 0 0 2px hsl(var(--primary) / 0.3), 0 0 30px hsl(var(--primary) / 0.1); }
      `}</style>

      <div className="w-full max-w-md relative z-10">
        {/* Header */}
        <div className="text-center mb-8 anim-fade-up">
          <h1 className="font-bold text-3xl text-white tracking-tight">
            <GlitchText text="NEXU" className="text-white" /><span className="text-primary">ZAP</span>
          </h1>
          {step < 5 && linkInfo && (
            <div className="flex items-center justify-center gap-2 mt-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">{trialLabel}</span>
            </div>
          )}
        </div>

        {/* Progress */}
        {step < 4 && <div className="mb-8"><ProgressDots current={step} total={4} /></div>}

        {/* STEP 0-3: Questions */}
        {step < 4 && (
          <div key={step} className={animDir === 'next' ? 'anim-slide-next' : 'anim-slide-prev'}>
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-3xl p-8 shadow-2xl">
              <div className="flex items-center gap-3 mb-2">
                {step > 0 && (
                  <button onClick={handleBack} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors">
                    <ArrowLeft className="w-4 h-4 text-white/50" />
                  </button>
                )}
                <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                  {(() => { const Icon = STEPS[step].icon; return <Icon className="w-5 h-5 text-primary" />; })()}
                </div>
              </div>

              <h2 className="text-xl font-bold text-white mt-4 mb-1">
                <TypewriterText text={STEPS[step].label} speed={25} />
              </h2>
              <p className="text-white/30 text-sm mb-6">
                {step === 0 && 'Esse sera seu nome de acesso na plataforma'}
                {step === 1 && 'Escolha uma senha que voce lembre facilmente'}
                {step === 2 && 'Confirme sua senha para garantir seguranca'}
                {step === 3 && 'Para verificacao e recuperacao de conta'}
              </p>

              {inputReady && (
                <div className="space-y-4 anim-fade-up">
                  <div className="relative">
                    <Input
                      ref={inputRef}
                      type={STEPS[step].type === 'password' ? ((step === 1 ? showPw : showPw2) ? 'text' : 'password') : STEPS[step].type}
                      placeholder={STEPS[step].placeholder}
                      value={values[STEPS[step].id]}
                      onChange={(e) => setValues({ ...values, [STEPS[step].id]: e.target.value })}
                      onKeyDown={handleKeyDown}
                      className="h-14 bg-white/[0.03] border-white/[0.08] rounded-2xl text-white text-lg pl-5 pr-12 focus:border-primary/40 focus:bg-white/[0.05] transition-all code-input"
                      autoFocus
                    />
                    {STEPS[step].type === 'password' && (
                      <button type="button" onClick={() => step === 1 ? setShowPw(!showPw) : setShowPw2(!showPw2)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-primary transition-colors">
                        {(step === 1 ? showPw : showPw2) ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    )}
                  </div>

                  <Button onClick={handleNext} disabled={!canProceed()}
                    className="w-full h-12 rounded-2xl text-base font-bold bg-primary hover:bg-primary/90 disabled:opacity-30 transition-all group">
                    {step < 3 ? 'Continuar' : 'Revisar Dados'}
                    <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              )}

              <p className="text-center text-white/15 text-xs mt-6 font-mono">
                Passo {step + 1} de 4 — pressione Enter para continuar
              </p>
            </div>
          </div>
        )}

        {/* STEP 4: Review */}
        {step === 4 && (
          <div className="anim-scale-in">
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-3xl p-8 shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <button onClick={handleBack} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors">
                  <ArrowLeft className="w-4 h-4 text-white/50" />
                </button>
                <h2 className="text-xl font-bold text-white">Confirme seus dados</h2>
              </div>

              <div className="space-y-4">
                {[
                  { label: 'Usuario', value: values.username, icon: User },
                  { label: 'Email', value: values.email, icon: Mail },
                  { label: 'Senha', value: '••••••••', icon: Lock },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-white/[0.02] border border-white/[0.05] anim-fade-up" style={{ animationDelay: `${i * 100}ms` }}>
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-white/30 uppercase tracking-wider">{item.label}</p>
                      <p className="text-white font-medium truncate">{item.value}</p>
                    </div>
                    <Check className="w-5 h-5 text-primary ml-auto shrink-0" />
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 rounded-2xl bg-primary/5 border border-primary/10">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <p className="text-sm text-primary font-medium">Ativacao com {trialLabel}</p>
                </div>
              </div>

              <Button onClick={handleConfirmEmail} className="w-full h-12 rounded-2xl text-base font-bold mt-6 bg-primary hover:bg-primary/90">
                Confirmar e Criar Conta
              </Button>
            </div>
          </div>
        )}

        {/* STEP 5: Email Confirmation Dialog */}
        {step === 5 && (
          <div className="anim-scale-in">
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-3xl p-8 shadow-2xl text-center">
              <div className="w-20 h-20 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6 anim-fade-up">
                <Mail className="w-10 h-10 text-primary" />
              </div>

              <h2 className="text-xl font-bold text-white mb-2 anim-fade-up" style={{ animationDelay: '100ms' }}>Confirmacao de Email</h2>
              <p className="text-white/50 text-sm mb-6 anim-fade-up" style={{ animationDelay: '200ms' }}>
                Enviaremos um codigo de verificacao para:
              </p>

              <div className="bg-white/[0.04] border border-primary/20 rounded-2xl p-4 mb-6 anim-fade-up" style={{ animationDelay: '300ms' }}>
                <p className="text-primary font-bold text-lg">{values.email}</p>
              </div>

              <p className="text-white/40 text-xs mb-6 anim-fade-up" style={{ animationDelay: '400ms' }}>
                Voce tem acesso a esse email? Um codigo de 6 digitos sera enviado para confirmar sua identidade. O codigo expira em 10 minutos.
              </p>

              <div className="flex gap-3 anim-fade-up" style={{ animationDelay: '500ms' }}>
                <Button onClick={() => setStep(3)} variant="outline" className="flex-1 h-12 rounded-2xl border-white/10 hover:border-white/20">
                  Trocar Email
                </Button>
                <Button onClick={handleProceedRegister} disabled={loading} className="flex-1 h-12 rounded-2xl bg-primary hover:bg-primary/90 font-bold">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Confirmar'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 6: Code Verification */}
        {step === 6 && (
          <div className="anim-scale-in">
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-3xl p-8 shadow-2xl text-center">
              <div className="w-20 h-20 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mx-auto mb-6">
                <Shield className="w-10 h-10 text-cyan-400" />
              </div>

              <h2 className="text-xl font-bold text-white mb-2">Digite o codigo</h2>
              <p className="text-white/50 text-sm mb-6">
                Enviamos para <span className="text-primary font-medium">{values.email}</span>
              </p>

              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                placeholder="000000"
                className="h-16 text-center text-3xl font-mono tracking-[0.6em] bg-white/[0.03] border-white/[0.08] rounded-2xl text-white mb-4 code-input"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) handleVerifyCode(); }}
              />

              <Button onClick={handleVerifyCode} disabled={verifying || code.length !== 6}
                className="w-full h-12 rounded-2xl text-base font-bold bg-primary hover:bg-primary/90 disabled:opacity-30 mb-4">
                {verifying ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verificar'}
              </Button>

              <button onClick={handleResend} disabled={resending} className="text-sm text-white/30 hover:text-primary transition-colors">
                {resending ? 'Reenviando...' : 'Nao recebeu? Reenviar codigo'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 7: First Login - Credentials */}
        {step === 7 && (
          <div className="anim-scale-in">
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-3xl p-8 shadow-2xl">
              <div className="text-center mb-6">
                <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4 anim-success-pulse">
                  <Check className="w-10 h-10 text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-1">Conta Criada!</h2>
                <p className="text-white/40 text-sm">Guarde suas credenciais de acesso</p>
              </div>

              <div className="space-y-3">
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.05] anim-fade-up">
                  <p className="text-xs text-white/30 uppercase tracking-wider mb-1">Usuario</p>
                  <p className="text-white font-bold text-lg font-mono">{values.username}</p>
                </div>
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.05] anim-fade-up" style={{ animationDelay: '100ms' }}>
                  <p className="text-xs text-white/30 uppercase tracking-wider mb-1">Email</p>
                  <p className="text-white font-bold font-mono">{values.email}</p>
                </div>
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.05] anim-fade-up" style={{ animationDelay: '200ms' }}>
                  <p className="text-xs text-white/30 uppercase tracking-wider mb-1">Senha</p>
                  <div className="flex items-center justify-between">
                    <p className="text-white font-bold text-lg font-mono">
                      {showPwModal ? values.password : '•'.repeat(values.password.length)}
                    </p>
                    <button onClick={() => setShowPwModal(!showPwModal)} className="text-white/30 hover:text-primary transition-colors">
                      {showPwModal ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 rounded-xl bg-amber-500/5 border border-amber-500/10 anim-fade-up" style={{ animationDelay: '300ms' }}>
                <p className="text-xs text-amber-400/80">
                  <Shield className="w-3 h-3 inline mr-1" />
                  Verificacao em 2 etapas ativada. Voce pode desativar em Configuracoes &gt; Perfil.
                </p>
              </div>

              <Button onClick={handleFinish} className="w-full h-12 rounded-2xl text-base font-bold mt-6 bg-primary hover:bg-primary/90 anim-fade-up" style={{ animationDelay: '400ms' }}>
                Acessar Painel
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-white/15 mt-8 font-mono">
          NexuZap &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
